import { useMemo } from 'react';
import { useMe } from './useQueries';

// What the signed-in user may do, from /api/users/me. The server is the real check; this only hides
// or disables what would be refused.
//
// canProject(projectId, right) takes 'view', a project right ('manage', 'finance',
// 'disciplines', which includes forecasting) or 'full' (every right). /me sends each membership's rights, already
// widened by admin / Edit All Projects, and what applies on every other project. While an admin views the
// app as someone else, user and every permission are that person's, and impersonatedBy is the admin.
const usePermissions = () => {
  const { data } = useMe();

  return useMemo(() => {
    const permissions = new Set(data?.permissions || []);
    const memberIds = new Set((data?.projectIds || []).map(Number));
    const byProject = new Map(Object.entries(data?.projectAccess || {}).map(([id, rights]) => [Number(id), rights]));
    const other = data?.otherProjectAccess || [];
    const projectAccess = (projectId) => byProject.get(Number(projectId)) || other;

    return {
      loaded: Boolean(data),
      user: data?.user || null,
      impersonatedBy: data?.impersonated_by || null,
      catalog: data?.catalog || [],
      ladders: data?.ladders || [],
      projectRights: data?.projectRights || [],
      can: (key) => permissions.has(key),
      projectAccess,
      isMember: (projectId) => memberIds.has(Number(projectId)),
      canProject: (projectId, right) => projectAccess(projectId).includes(right),
      // The rights this user may give or take away on a project: the ones they hold there.
      grantableRights: (projectId) =>
        (data?.projectRights || []).map((r) => r.key).filter((key) => projectAccess(projectId).includes(key)),
    };
  }, [data]);
};

export const canCreateProjects = (p) => p.can('projects.create');
export const canOpenUserManagement = (p) => p.can('users.manage') || p.can('rates.manage');

export default usePermissions;
